import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, apiBaseUrl, apiGet } from "./api";
import type { ID } from "./id";

/**
 * While a competition runs (Probíhá), the organizer's screens follow its
 * live log (docs/zivy-provoz-soutezi.md §7): every event re-reads the start
 * list, the categories and the přihlášky, so a call, a strike-off or a move
 * made in the hall shows at once instead of on the next minute's poll.
 * Nothing is applied from the event itself: the page reads the same
 * endpoints it always does, and the stream only says when.
 *
 * The stream is for those who may view the competition (a session the live
 * read endpoints accept: its managers and the read-only viewers); everybody
 * else keeps the polling.
 */

/** Two keepalives (5 s each, §7) and a margin: a stream quiet longer is dead. */
const SILENCE_MS = 12_000;
/** Events often come in bursts (a regroup writes several): one re-read,
 * BATCH_MS after the last of them and never later than MAX_WAIT_MS after
 * the first, so a busy hall cannot postpone it for good. */
const BATCH_MS = 300;
const MAX_WAIT_MS = 1_000;
/** Events that change none of the documents followed (scores, deductions,
 * devices, results): no re-read. Anything else re-reads, a new kind
 * included, which is the safe way to be wrong. */
const IGNORED = ["score.", "deduction.", "device.", "category.results_"];
/** Pause before starting over from a new snapshot. */
const RETRY_MS = 3_000;

/**
 * `hold` defers the re-reads while it is true (an organizer dragging a
 * block: a re-read would reset the preview under the pointer); what came
 * meanwhile is read once it is released.
 */
export function useFollowLive(competitionId: ID | undefined, enabled: boolean, hold = false) {
  const qc = useQueryClient();
  const held = useRef(hold);
  held.current = hold;
  const owed = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!hold && owed.current) {
      const run = owed.current;
      owed.current = null;
      run();
    }
  }, [hold]);
  useEffect(() => {
    if (!competitionId || !enabled || typeof EventSource === "undefined") return;
    let closed = false;
    let source: EventSource | null = null;
    let watchdog: ReturnType<typeof setTimeout> | undefined;
    let batch: ReturnType<typeof setTimeout> | undefined;
    let deadline: ReturnType<typeof setTimeout> | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;

    const reread = () => {
      clearTimeout(batch);
      clearTimeout(deadline);
      batch = deadline = undefined;
      if (held.current) {
        owed.current = reread;
        return;
      }
      void qc.invalidateQueries({ queryKey: ["startlists", competitionId] });
      void qc.invalidateQueries({ queryKey: ["competitions", "categories", competitionId] });
      void qc.invalidateQueries({ queryKey: ["competitions", "entries", competitionId] });
    };
    const refresh = () => {
      clearTimeout(batch);
      batch = setTimeout(reread, BATCH_MS);
      deadline ??= setTimeout(reread, MAX_WAIT_MS);
    };
    const restart = () => {
      source?.close();
      source = null;
      clearTimeout(watchdog);
      if (closed) return;
      clearTimeout(retry);
      retry = setTimeout(() => void open(), RETRY_MS);
    };
    const heard = () => {
      clearTimeout(watchdog);
      watchdog = setTimeout(restart, SILENCE_MS);
    };

    const open = async () => {
      let head: string;
      try {
        // The resume point: the log's head now. What came before is in
        // what the page already reads.
        head = (await apiGet<{ id: string }>(`/live/${competitionId}/snapshot`)).id;
      } catch (e) {
        // A session gone or a role taken away: nothing to follow, and
        // retrying would reopen the session dialog every few seconds (the
        // client reports every 401). The page's own reads say why.
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
          closed = true;
          return;
        }
        restart();
        return;
      }
      if (closed) return;
      // One fresh read covers what came between the page's own reads and
      // the head: after a gap, and the first time too, since the page may
      // have read before an event the head already includes.
      refresh();
      const s = new EventSource(
        `${apiBaseUrl}/live/${competitionId}/stream?after=${encodeURIComponent(head)}`,
        { withCredentials: true },
      );
      source = s;
      heard();
      s.addEventListener("keepalive", heard);
      s.onmessage = (m: MessageEvent<string>) => {
        heard();
        let type = "";
        try {
          type = (JSON.parse(m.data) as { type?: string }).type ?? "";
        } catch {
          // unreadable: re-read, as for an unknown kind
        }
        if (!IGNORED.some((p) => type.startsWith(p))) refresh();
      };
      // Access lost (the session expired, a role was taken away): nothing
      // more to follow. One last re-read lets the page find out through its
      // own requests, the session dialog included.
      s.addEventListener("revoked", () => {
        closed = true;
        s.close();
        clearTimeout(watchdog);
        reread();
      });
      // EventSource reconnects on its own (with Last-Event-ID) after a
      // dropped connection; it gives up for good on a refusal (a resume
      // point not in the log, a 403), and then it starts over.
      s.onerror = () => {
        if (s.readyState === EventSource.CLOSED) restart();
      };
    };
    void open();

    return () => {
      closed = true;
      source?.close();
      clearTimeout(watchdog);
      clearTimeout(batch);
      clearTimeout(deadline);
      clearTimeout(retry);
      owed.current = null;
    };
  }, [qc, competitionId, enabled]);
}
